/**
 * Comprehensive onboarding utilities for managing user progress and routing
 * Combines storage management and step routing functionality
 * 
 * STORAGE: Data is stored in localStorage for unauthenticated users and migrated to database upon authentication
 * ROUTING: Provides consistent step-to-route mapping across the application
 */

// ============================================================================
// STORAGE CONSTANTS
// ============================================================================

const STORAGE_KEY = 'onboardingData';
const TIMEOUT_HOURS = 24;

// ============================================================================
// STEP MAPPING CONSTANTS
// ============================================================================

/**
 * Maps step numbers to semantic route names for the 8-step onboarding flow
 */
const ONBOARDING_STEP_MAP = {
  1: 'platform',
  2: 'username', 
  3: 'leagues',
  4: 'auth',
  5: 'roast-level',
  6: 'league-context',
  7: 'weekly-context',
  8: 'plan'
};

// ============================================================================
// STORAGE FUNCTIONS
// ============================================================================

/**
 * Save onboarding data for a specific step
 * @param {string} step - The step identifier (e.g., 'platform', 'username', 'leagues', 'auth', 'roast-level', 'league-context', 'weekly-context', 'plan')
 * @param {object} data - The data to store for this step
 */
export const saveOnboardingStep = (step, data) => {
  try {
    const existingData = getOnboardingData();
    const updatedData = {
      ...existingData,
      [step]: data,
      timestamp: Date.now()
    };
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedData));
    return true;
  } catch (error) {
    console.error('Error saving onboarding data:', error);
    return false;
  }
};

/**
 * Get all stored onboarding data
 * @returns {object|null} The stored onboarding data or null if none exists
 */
export const getOnboardingData = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return null;
    
    const parsedData = JSON.parse(data);
    
    // Check if data has expired (24 hours)
    if (isOnboardingDataExpired(parsedData.timestamp)) {
      clearOnboardingData();
      return null;
    }
    
    return parsedData;
  } catch (error) {
    console.error('Error retrieving onboarding data:', error);
    return null;
  }
};

/**
 * Get data for a specific step
 * @param {string} step - The step identifier (e.g., 'platform', 'username', 'leagues', 'auth', 'roast-level', 'league-context', 'weekly-context', 'plan')
 * @returns {object|null} The data for the specified step or null if not found
 */
export const getOnboardingStepData = (step) => {
  const data = getOnboardingData();
  return data ? data[step] : null;
};

/**
 * Check if onboarding data has expired
 * @param {number} timestamp - The timestamp to check
 * @returns {boolean} True if data has expired
 */
export const isOnboardingDataExpired = (timestamp) => {
  if (!timestamp) return true;
  
  const now = Date.now();
  const expirationTime = timestamp + (TIMEOUT_HOURS * 60 * 60 * 1000);
  
  return now > expirationTime;
};

/**
 * Check if stored onboarding data is still valid
 * @returns {boolean} True if data exists and hasn't expired
 */
export const isOnboardingDataValid = () => {
  const data = getOnboardingData();
  return data !== null && !isOnboardingDataExpired(data.timestamp);
};

/**
 * Clear all stored onboarding data
 */
export const clearOnboardingData = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    return true;
  } catch (error) {
    console.error('Error clearing onboarding data:', error);
    return false;
  }
};

/**
 * Check if user should use database storage (already authenticated)
 * @param {object} auth - The authentication state
 * @returns {boolean} True if user should use database storage
 */
export const shouldUseDatabase = (auth) => {
  return auth && auth.permission && auth.user?.id;
};

/**
 * Get the current onboarding step from stored data
 * @returns {string} The current step identifier
 */
export const getCurrentOnboardingStep = () => {
  const data = getOnboardingData();
  if (!data) return 'platform';
  
  // Determine step based on what data exists (8-step flow)
  if (data.platform && data.username && data.leagues && data.auth && data['roast-level'] && data['league-context'] && data['weekly-context']) return 'plan';
  if (data.platform && data.username && data.leagues && data.auth && data['roast-level'] && data['league-context']) return 'weekly-context';
  if (data.platform && data.username && data.leagues && data.auth && data['roast-level']) return 'league-context';
  if (data.platform && data.username && data.leagues && data.auth) return 'roast-level';
  if (data.platform && data.username && data.leagues) return 'auth';
  if (data.platform && data.username) return 'leagues';
  if (data.platform) return 'username';
  return 'platform';
};

/**
 * Check if a specific step has been completed
 * @param {string} stepName - The step identifier to check
 * @returns {boolean} True if the step has been completed
 */
export const isStepCompleted = (stepName) => {
  const data = getOnboardingData();
  
  // Handle both new semantic names and legacy numbered names for backward compatibility
  if (stepName === 'platform' || stepName === 'step1') {
    return data && (data.platform || data.step1);
  }
  if (stepName === 'username' || stepName === 'step2') {
    return data && (data.username || data.step2);
  }
  if (stepName === 'leagues' || stepName === 'step3') {
    return data && (data.leagues || data.step3);
  }
  if (stepName === 'auth' || stepName === 'step4') {
    return data && (data.auth || data.step4);
  }
  if (stepName === 'roast-level') {
    return data && data['roast-level'];
  }
  if (stepName === 'league-context') {
    return data && data['league-context'];
  }
  if (stepName === 'weekly-context') {
    return data && data['weekly-context'];
  }
  if (stepName === 'plan' || stepName === 'step5') {
    return data && (data.plan || data.step5);
  }
  
  return data && data[stepName];
};

/**
 * Get summary of all collected onboarding data
 * @returns {object} Summary of collected data
 */
export const getOnboardingSummary = () => {
  const data = getOnboardingData();
  if (!data) return null;
  
  return {
    platform: data.platform || data.step1 || null,
    username: data.username || data.step2 || null,
    leagues: data.leagues || data.step3 || null,
    auth: data.auth || data.step4 || null,
    'roast-level': data['roast-level'] || null,
    'league-context': data['league-context'] || null,
    'weekly-context': data['weekly-context'] || null,
    plan: data.plan || data.step5 || null,
    timestamp: data.timestamp,
    isComplete: !!(data.platform && data.username && data.leagues && data.auth && data['roast-level'] && data['league-context'] && data['weekly-context'])
  };
};

/**
 * Get the next step in the onboarding flow
 * @returns {string} The next step identifier
 */
export const getNextStep = () => {
  const data = getOnboardingData();
  if (!data) return 'platform';
  
  if (!data.platform && !data.step1) return 'platform';
  if (!data.username && !data.step2) return 'username';
  if (!data.leagues && !data.step3) return 'leagues';
  if (!data.auth && !data.step4) return 'auth';
  if (!data['roast-level']) return 'roast-level';
  if (!data['league-context']) return 'league-context';
  if (!data['weekly-context']) return 'weekly-context';
  if (!data.plan && !data.step5) return 'plan';
  
  return 'complete';
};

/**
 * Get the previous step in the onboarding flow
 * @returns {string} The previous step identifier
 */
export const getPreviousStep = () => {
  const data = getOnboardingData();
  if (!data) return null;
  
  if (data.plan || data.step5) return 'weekly-context';
  if (data['weekly-context']) return 'league-context';
  if (data['league-context']) return 'roast-level';
  if (data['roast-level']) return 'auth';
  if (data.auth || data.step4) return 'leagues';
  if (data.leagues || data.step3) return 'username';
  if (data.username || data.step2) return 'platform';
  
  return null;
};

// ============================================================================
// ROUTING FUNCTIONS
// ============================================================================

/**
 * Get the onboarding route for a given step number
 * @param {number} step - The onboarding step number (1-8)
 * @returns {string} - The route path (e.g., '/onboarding/platform')
 */
export const getOnboardingRoute = (step) => {
  const routeName = ONBOARDING_STEP_MAP[step] || 'platform';
  return `/onboarding/${routeName}`;
};

/**
 * Get the route name for a given step number
 * @param {number} step - The onboarding step number (1-8)
 * @returns {string} - The route name (e.g., 'platform')
 */
export const getOnboardingRouteName = (step) => {
  return ONBOARDING_STEP_MAP[step] || 'platform';
};

// ============================================================================
// EXPORTS
// ============================================================================

const onboardingUtils = {
  // Storage functions
  saveOnboardingStep,
  getOnboardingData,
  getOnboardingStepData,
  isOnboardingDataExpired,
  isOnboardingDataValid,
  clearOnboardingData,
  shouldUseDatabase,
  getCurrentOnboardingStep,
  isStepCompleted,
  getOnboardingSummary,
  getNextStep,
  getPreviousStep,
  
  // Routing functions
  ONBOARDING_STEP_MAP,
  getOnboardingRoute,
  getOnboardingRouteName
};

export default onboardingUtils;
